/* Owner ERP — Reports, Audit viewer, Billing/Plan sections */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, Crown, CheckCircle2, LogOut, Building2, Upload } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { API } from '../../utils/api';
import { GuestContact } from '../../components/GuestDetails';
import { fmtKr, fmtDate, Panel, SectionTitle, Meter, WORK_SANS, auditActionLabel, auditDetail } from './ownerUi';

const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (d?.message) return d.message;
  return 'Error';
};

/* ============================== Reports ============================== */

export function ReportsSection({ headers, t, lang, range, reportDivision }) {
  const [subs, setSubs] = useState([]);
  const [items, setItems] = useState(null);
  const [division, setDivision] = useState(reportDivision || 'all');
  const [direction, setDirection] = useState('received');

  const load = useCallback(async () => {
    try {
      const params = { date_from: range.from, date_to: range.to, limit: 100 };
      if (division !== 'all') params.sub_account_id = division;
      if (direction !== 'all') params.direction = direction;
      const [tx, s] = await Promise.all([
        axios.get(`${API}/business/transactions`, { headers, params }),
        axios.get(`${API}/business/sub-accounts`, { headers, params: { status: 'ACTIVE' } }),
      ]);
      setItems(tx.data.items || []);
      setSubs(s.data.items || []);
    } catch (e) { console.error(e); toast.error(errMsg(e)); }
  }, [headers, range.from, range.to, division, direction]);
  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    try {
      const params = { format: 'csv' };
      if (division !== 'all') params.sub_account_id = division;
      const res = await axios.get(`${API}/business/transactions/export`, { headers, params, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `paymig-report-${range.from}-${range.to}.csv`;
      a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const total = (items || []).reduce((a, x) => a + (x.amount || 0), 0);

  return (
    <div className="space-y-4">
      <Panel testId="owner-reports">
        <div className="px-5 pt-5 pb-3 flex flex-wrap items-center gap-3">
          <SectionTitle sub={`${range.from} → ${range.to}`}>{t.reports_title}</SectionTitle>
          <div className="ml-auto flex items-center gap-2">
            <Select value={division} onValueChange={setDivision}>
              <SelectTrigger data-testid="owner-report-division" className="h-9 w-[190px] text-[13px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t.all_divisions}</SelectItem>
                {subs.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={direction} onValueChange={setDirection}>
              <SelectTrigger data-testid="owner-report-direction" className="h-9 w-[130px] text-[13px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="received">{t.received}</SelectItem>
                <SelectItem value="sent">{t.sent}</SelectItem>
                <SelectItem value="all">{t.all}</SelectItem>
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" data-testid="owner-export-csv" className="h-9 text-[13px] border-slate-200" onClick={exportCsv}>
              <Download size={14} className="mr-1.5" />{t.export_csv}
            </Button>
          </div>
        </div>
        <div className="px-5 pb-2">
          <Badge className="bg-teal-50 text-teal-700 border border-teal-200 shadow-none hover:bg-teal-50">
            {(items || []).length} {t.tx_short} · {fmtKr(total)}
          </Badge>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-left text-[11.5px] uppercase tracking-wide text-slate-400 border-y border-slate-100 bg-slate-50/60">
                <th className="px-5 py-2.5 font-semibold">{t.date}</th>
                <th className="px-3 py-2.5 font-semibold">{t.from_to}</th>
                <th className="px-3 py-2.5 font-semibold">{t.description}</th>
                <th className="px-3 py-2.5 font-semibold">{t.sub_account}</th>
                <th className="px-3 py-2.5 font-semibold text-right">{t.amount}</th>
              </tr>
            </thead>
            <tbody>
              {(items || []).map((x, i) => (
                <tr key={x.id || i} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60">
                  <td className="px-5 py-2.5 text-slate-500 whitespace-nowrap">{fmtDate(x.created_at || x.date, lang)}</td>
                  <td className="px-3 py-2.5">
                    {x.is_guest
                      ? <GuestContact txn={x} guestLabel={t.guest} className="" testId={`owner-txn-guest-${i}`} />
                      : (x.from_name || x.to_name || x.recipient_name || '—')}
                  </td>
                  <td className="px-3 py-2.5 text-slate-600 max-w-[260px] truncate">{x.description || '—'}</td>
                  <td className="px-3 py-2.5 text-slate-600">{x.sub_account_name || '—'}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums font-semibold text-teal-700">{fmtKr(x.amount)}</td>
                </tr>
              ))}
              {items && items.length === 0 && (
                <tr><td colSpan={5} className="px-5 py-8 text-center text-slate-400">{t.no_data}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}

/* ============================== Audit ============================== */

export function AuditSection({ orgId, headers, t, lang }) {
  const [items, setItems] = useState(null);
  const [skip, setSkip] = useState(0);
  const [done, setDone] = useState(false);
  const PAGE = 50;

  const load = useCallback(async (offset) => {
    try {
      const res = await axios.get(`${API}/org/${orgId}/audit`, { headers, params: { limit: PAGE, skip: offset } });
      const batch = res.data.items || [];
      setItems((prev) => (offset === 0 ? batch : [...(prev || []), ...batch]));
      if (batch.length < PAGE) setDone(true);
    } catch (e) { console.error(e); toast.error(errMsg(e)); }
  }, [orgId, headers]);
  useEffect(() => { load(0); }, [load]);

  const ACTION_TONE = {
    org_member_invited: 'bg-sky-50 text-sky-700',
    org_invite_accepted: 'bg-teal-50 text-teal-700',
    org_invite_auto_attached: 'bg-teal-50 text-teal-700',
    org_invite_rejected: 'bg-amber-50 text-amber-700',
    org_invite_cancelled: 'bg-slate-100 text-slate-600',
    org_member_removed: 'bg-rose-50 text-rose-700',
    org_member_role_changed: 'bg-violet-50 text-violet-700',
    org_sub_account_created: 'bg-teal-50 text-teal-700',
    org_event_created: 'bg-teal-50 text-teal-700',
    org_event_updated: 'bg-sky-50 text-sky-700',
    org_payout_swish_changed: 'bg-violet-50 text-violet-700',
    org_member_suspended: 'bg-rose-50 text-rose-700',
    org_member_reactivated: 'bg-teal-50 text-teal-700',
    org_invite_resent: 'bg-sky-50 text-sky-700',
  };

  return (
    <Panel testId="owner-audit">
      <div className="px-5 pt-5 pb-3">
        <SectionTitle sub={t.audit_sub}>{t.audit_title}</SectionTitle>
      </div>
      <div className="px-5 pb-5 space-y-1">
        {(items || []).map((a) => (
          <div key={a.id} className="flex items-start gap-3 px-2 py-2.5 rounded-lg hover:bg-slate-50 border-b border-slate-50 last:border-0">
            <span className={`text-[11px] font-semibold px-2 py-1 rounded-md shrink-0 ${ACTION_TONE[a.action] || 'bg-slate-100 text-slate-600'}`}>
              {auditActionLabel(a.action, t)}
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-[12.5px] text-slate-700 truncate">
                {a.details?.actor_name && <span className="font-semibold">{a.details.actor_name} · </span>}
                {auditDetail(a.details, t) || '—'}
              </p>
            </div>
            <span className="text-[11.5px] text-slate-400 shrink-0 whitespace-nowrap">{fmtDate(a.timestamp, lang)}</span>
          </div>
        ))}
        {items && items.length === 0 && <p className="text-[13px] text-slate-400 py-8 text-center">{t.no_data}</p>}
        {!done && items && items.length > 0 && (
          <div className="pt-2 text-center">
            <Button variant="outline" size="sm" className="h-8 text-[13px] border-slate-200"
                    onClick={() => { const n = skip + PAGE; setSkip(n); load(n); }}>{t.load_more}</Button>
          </div>
        )}
      </div>
    </Panel>
  );
}

/* ============================== Billing / Plan ============================== */

export function BillingSection({ orgId, headers, t, lang }) {
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState('monthly');
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/org/${orgId}/billing`, { headers });
      setData(res.data);
      setPeriod(res.data.billing_period || 'monthly');
    } catch (e) { console.error(e); toast.error(errMsg(e)); }
  }, [orgId, headers]);
  useEffect(() => { load(); }, [load]);

  const choose = async (plan) => {
    setBusy(plan.id);
    try {
      const res = await axios.post(`${API}/subscription/create-checkout`,
        { plan_id: plan.id, origin_url: window.location.origin, billing_period: period }, { headers });
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        toast.success(t.plan_changed);
        load();
      }
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };

  if (!data) return <p className="text-sm text-slate-400">{t.loading}</p>;
  const cur = data.current_plan || {};
  const usage = data.usage || {};
  const lim = (v) => (v === null || v === undefined || v < 0 ? t.unlimited : v);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Panel className="p-5" testId="owner-current-plan">
          <div className="flex items-center justify-between mb-3">
            <SectionTitle>{t.current_plan}</SectionTitle>
            <Badge className="bg-teal-600 text-white shadow-none hover:bg-teal-600 gap-1"><Crown size={11} />{cur.name || '—'}</Badge>
          </div>
          <div className="text-[26px] font-semibold tabular-nums" style={WORK_SANS}>
            {fmtKr(period === 'annual' ? cur.cost_annual : cur.cost_monthly)}
            <span className="text-[13px] text-slate-400 font-normal">{period === 'annual' ? t.per_year : t.per_month}</span>
          </div>
          {data.subscription_end && (
            <p className="text-[12px] text-slate-400 mt-1">{t.renews}: {fmtDate(data.subscription_end, lang)}</p>
          )}
          <div className="mt-4 space-y-3">
            {[['seats', t.seats_used], ['divisions', t.divisions_used], ['swish_accounts', t.swish_used]].map(([key, label]) => (
              <div key={key}>
                <div className="flex justify-between text-[12.5px] mb-1.5">
                  <span className="text-slate-500">{t.usage}: {label}</span>
                  <span className="font-semibold tabular-nums">{usage[key]?.used ?? 0} / {lim(usage[key]?.limit)}</span>
                </div>
                <Meter used={usage[key]?.used ?? 0} limit={usage[key]?.limit} />
              </div>
            ))}
          </div>
        </Panel>

        <Panel className="p-5" testId="owner-billing-history">
          <SectionTitle>{t.plan_history}</SectionTitle>
          <div className="mt-3 space-y-2">
            {(data.history || []).length === 0 && <p className="text-[13px] text-slate-400 py-6 text-center">{t.no_history}</p>}
            {(data.history || []).map((h) => (
              <div key={h.id} className="flex items-center justify-between border border-slate-100 rounded-lg px-3 py-2.5">
                <div>
                  <div className="text-[13px] font-medium">{h.plan_name || '—'} <span className="text-slate-400">· {h.billing_period || ''}</span></div>
                  <div className="text-[11.5px] text-slate-400">{fmtDate(h.created_at, lang)} · {h.payment_status}</div>
                </div>
                <span className="text-[13px] font-semibold tabular-nums">{fmtKr(h.amount)}</span>
              </div>
            ))}
          </div>
        </Panel>
      </div>

      <Panel className="p-5" testId="owner-plans">
        <div className="flex items-center justify-between mb-4">
          <SectionTitle>{t.billing_title}</SectionTitle>
          <div className="flex items-center rounded-lg border border-slate-200 bg-white overflow-hidden">
            {[['monthly', t.billing_monthly], ['annual', t.billing_annual]].map(([key, label]) => (
              <button key={key} onClick={() => setPeriod(key)}
                className={`px-3 py-1.5 text-[12px] font-semibold transition-colors ${period === key ? 'bg-teal-600 text-white' : 'text-slate-500 hover:bg-slate-50'}`}>
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {(data.plans || []).map((p) => {
            const isCurrent = p.id === cur.id;
            return (
              <div key={p.id} data-testid={`owner-plan-${p.id}`}
                   className={`border rounded-xl p-4 flex flex-col gap-2 ${isCurrent ? 'border-teal-500 bg-teal-50/40' : 'border-slate-200'}`}>
                <div className="flex items-center justify-between">
                  <span className="text-[14px] font-semibold" style={WORK_SANS}>{p.name}</span>
                  {isCurrent && <Badge className="bg-teal-600 text-white shadow-none hover:bg-teal-600 text-[10px]">{t.current}</Badge>}
                </div>
                <div className="text-[20px] font-semibold tabular-nums" style={WORK_SANS}>
                  {fmtKr(period === 'annual' ? p.cost_annual : p.cost_monthly)}
                  <span className="text-[12px] text-slate-400 font-normal">{period === 'annual' ? t.per_year : t.per_month}</span>
                </div>
                <div className="text-[12px] text-slate-500 space-y-1 flex-1">
                  <div className="flex items-center gap-1.5"><CheckCircle2 size={12} className="text-teal-600" />{lim(p.max_divisions)} {t.divisions_used}</div>
                  <div className="flex items-center gap-1.5"><CheckCircle2 size={12} className="text-teal-600" />{lim(p.max_org_seats)} {t.seats_used}</div>
                  <div className="flex items-center gap-1.5"><CheckCircle2 size={12} className="text-teal-600" />{lim(p.max_swish_accounts)} {t.swish_used}</div>
                </div>
                {!isCurrent && (
                  <Button size="sm" className="bg-teal-700 hover:bg-teal-800 text-white h-8 text-[13px]"
                          disabled={busy === p.id} onClick={() => choose(p)}>{t.choose_plan}</Button>
                )}
              </div>
            );
          })}
        </div>
      </Panel>
    </div>
  );
}

/* ============================== Owner / Business Profile ============================== */

export function OwnerProfileSection({ headers, t, onLogout, onLogoChange }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const uploadLogo = async (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error('Max 5MB'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const res = await axios.post(`${API}/business/logo`, fd, { headers });
      const url = res.data.logo_path ? `${API}/business/logo-file/${res.data.logo_path}` : '';
      setForm((prev) => ({ ...prev, logo_path: res.data.logo_path }));
      if (onLogoChange) onLogoChange(url);
      toast.success(t.saved);
    } catch (err) { toast.error(errMsg(err)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const removeLogo = async () => {
    try {
      await axios.delete(`${API}/business/logo`, { headers });
      setForm((prev) => ({ ...prev, logo_path: '' }));
      if (onLogoChange) onLogoChange('');
    } catch (err) { toast.error(errMsg(err)); }
  };

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/business/profile`, { headers });
      setForm(res.data);
    } catch (e) { toast.error(errMsg(e)); }
  }, [headers]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/business/profile`, {
        company_name: form.company_name,
        org_number: form.org_number,
        address: form.address,
        full_name: form.full_name,
      }, { headers });
      toast.success(t.saved);
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  if (!form) return <p className="text-sm text-slate-400">{t.loading}</p>;

  const logoUrl = form.logo_path ? `${API}/business/logo-file/${form.logo_path}` : '';

  const fld = (k, label, disabled) => (
    <div key={k}>
      <label className="text-[12px] font-medium text-slate-500">{label}</label>
      <Input className="mt-1 h-9 text-[13px]" value={form[k] || ''} disabled={disabled}
             onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`owner-profile-${k}`} />
    </div>
  );

  return (
    <div className="max-w-2xl space-y-4">
      <Panel className="p-5" testId="owner-profile">
        <SectionTitle sub={t.profile_sub}>{t.profile_title}</SectionTitle>
        <div className="mt-4 flex items-center gap-4" data-testid="owner-logo-row">
          <div className="w-16 h-16 rounded-xl border border-slate-200 bg-white overflow-hidden flex items-center justify-center shrink-0">
            {logoUrl
              ? <img src={logoUrl} alt="logo" className="w-full h-full object-contain" data-testid="owner-logo-preview" />
              : <Building2 size={24} className="text-slate-300" />}
          </div>
          <div>
            <div className="text-[12px] font-medium text-slate-500 mb-1.5">{t.company_logo}</div>
            <div className="flex items-center gap-2">
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={uploadLogo} data-testid="owner-logo-input" />
              <Button variant="outline" className="h-8 text-[12px]" disabled={uploading} onClick={() => fileRef.current && fileRef.current.click()} data-testid="owner-logo-upload">
                <Upload size={13} className="mr-1.5" />{uploading ? '…' : t.upload_logo}
              </Button>
              {logoUrl && (
                <Button variant="ghost" className="h-8 text-[12px] text-rose-600 hover:bg-rose-50" onClick={removeLogo} data-testid="owner-logo-remove">{t.remove}</Button>
              )}
            </div>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {fld('company_name', t.company_name)}
          {fld('org_number', t.org_number)}
          {fld('full_name', t.contact_name)}
          {fld('email', t.email, true)}
          {fld('phone', t.phone, true)}
          {fld('swish_merchant_number', t.merchant_number, true)}
        </div>
        <div className="mt-4">{fld('address', t.address)}</div>
        <div className="mt-5 flex items-center justify-between gap-3">
          <Button className="bg-teal-700 hover:bg-teal-800 text-white h-9 text-[13px]"
                  disabled={saving} onClick={save} data-testid="owner-profile-save">{t.save_changes}</Button>
          <Button variant="outline" className="h-9 text-[13px] text-rose-600 border-rose-200 hover:bg-rose-50"
                  onClick={onLogout} data-testid="owner-profile-logout"><LogOut size={14} className="mr-1.5" />{t.logout}</Button>
        </div>
      </Panel>
    </div>
  );
}
